/* eslint-disable prefer-destructuring */
/* eslint-disable react/no-this-in-sfc */
import React, { useMemo, useRef, useState } from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import HighchartsDrilldown from 'highcharts/modules/drilldown'
import { useSelector } from 'react-redux'
import { SizeMeProps, withSize } from 'react-sizeme'
import { endOfMonth } from '../../../../../config/dayjsConfig'

import { contextCompanyIdSelector } from '../../../../../redux/context/company/selectors'
import { customReportDateSelector } from '../../../../../redux/context/customReports/selectors'
import { useBackend } from '../../../../../services/backend'
import { DrillData, FormattedPoint, labelFormatter } from './chartUtils'
import { CustomReportPieSection } from '../../../../../redux/context/customReports/typesSection'
import { getDrillData, getPieSeries } from './utils'
import { formatVariableValue } from '../grid/utils/valueFormatting'
import { withPrintOverrides } from './printOverrides'

interface CustomPieChartProps {
  section: CustomReportPieSection
  size?: SizeMeProps['size']
  printMode?: boolean
}

HighchartsDrilldown(Highcharts)

/**
 * Piirakka graafi
 *
 * Yksi kategoria
 * Yritys -> Dimensio? -> Funktio(% tunnusluvusta)
 */
const CustomPieChart: React.FC<CustomPieChartProps> = ({ section, size, printMode }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null)
  const companyId = useSelector(contextCompanyIdSelector)
  const date = useSelector(customReportDateSelector)
  const [explainerData, setExplainerData] = useState<DrillData>({})
  const explainRequest = useBackend(
    `/api/companies/${companyId}/reporting/custom/reports/${section.reportId}/sections/${section.id}/explain`
  )

  const handleExplainRequest = async (variableId: number | string) => {
    return explainRequest.get({
      body: {
        params: {
          variableId,
          date: endOfMonth(date)
        }
      }
    })
  }

  const explainerDataRef = useRef<DrillData>(explainerData)

  // Update both state and ref
  const updateExplainerData = (newData: DrillData) => {
    const updated = { ...explainerDataRef.current, ...newData }
    explainerDataRef.current = updated
    setExplainerData(updated)
  }

  const drilldown: Highcharts.DrilldownCallbackFunction = async e => {
    const chart = chartRef.current?.chart
    if (!e.seriesOptions && chart) {
      const variableId: string = (e.point as any).drilldown.toString()
      let data: Highcharts.PointOptionsObject[]

      // Jos on eka id, ja explainia ei ole vielä haetaan data asynccinä,
      // muuten otetaan explain statesta
      if (variableId.split('-').length === 1 && !(variableId in explainerDataRef.current)) {
        chart.showLoading('Loading ...')
        try {
          const explain = await handleExplainRequest(variableId)
          const drillData = getDrillData(variableId, explain, section.categories)
          updateExplainerData(drillData)
          data = drillData[variableId]
        } finally {
          chart.hideLoading()
        }
      } else {
        data = explainerDataRef.current[variableId]
      }

      const series = {
        name: e.point.name,
        id: variableId,
        type: 'pie',
        data: data?.filter(d => d?.y !== null)
      } satisfies Highcharts.SeriesOptionsType

      chart.addSeriesAsDrilldown(e.point, series)
    }
  }

  const options = useMemo(
    () =>
      ({
        chart: {
          type: 'pie',
          width: printMode ? undefined : size?.width,
          height: printMode ? undefined : size?.height,
          events: {
            drilldown
          }
        },
        title: {
          text: ''
        },
        series: [getPieSeries(section)],
        drilldown: {
          series: []
        },
        plotOptions: {
          series: {
            animation: false
          },
          pie: {
            cursor: 'pointer',
            dataLabels: {
              enabled: section.style?.showDataLabels === 'true',
              formatter(this: Highcharts.PointLabelObject) {
                const custom = (this.point.options as FormattedPoint).custom
                const numberFormat = custom?.numberFormat ?? { decimals: 0 }
                const negative = (this.point as Highcharts.Point & { negative?: boolean }).negative
                const y = negative && this.y ? -this.y : this.y
                return `<b>${this.point.name}</b>: ${formatVariableValue(y, numberFormat, custom?.variableType)}`
              }
            },
            showInLegend: true
          }
        },
        tooltip: {
          formatter(this: Highcharts.TooltipFormatterContextObject) {
            const custom = (this.point.options as FormattedPoint).custom
            // Tooltip shows the full unscaled value — drop displayScale.
            const numberFormat = { decimals: custom?.numberFormat?.decimals ?? 0 }
            const negative = (this.point as Highcharts.Point & { negative?: boolean }).negative
            const y = negative && this.y ? -this.y : this.y
            const value = formatVariableValue(y, numberFormat, custom?.variableType)
            const percent = this.percentage?.toFixed(1)
            return `${this.key}: <b>${value}</b> (${percent}%)`
          }
        },
        legend: {
          labelFormatter() {
            return labelFormatter(this.options, this.name)
          }
        }
      } as Highcharts.Options),
    [section, size]
  )

  const finalOptions = withPrintOverrides(options, printMode)

  return (
    <div style={{ height: '100%' }}>
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={finalOptions}
        immutable={printMode}
        containerProps={printMode ? { style: { height: '100%' } } : undefined}
      />
    </div>
  )
}

export default withSize({ monitorHeight: true, noPlaceholder: true })(CustomPieChart)
